import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../bizsuite-app';
import DataTable from '../components/DataTable';
import Modal from '../components/Modal';
import FormField from '../components/FormField';
import { useFormDirty } from '../hooks/useFormDirty';
import { formatDate } from '../utils/date';

const STATUS_BADGES = { open: 'badge-green', closed: 'badge-gray', on_hold: 'badge-yellow' };
const CANDIDATE_STATUS_OPTIONS = ['applied', 'screening', 'interview', 'offer', 'joined', 'rejected'];
const CANDIDATE_BADGES = {
  applied: 'badge-blue', screening: 'badge-yellow', interview: 'badge-purple',
  offer: 'badge-green', joined: 'badge-green', rejected: 'badge-red',
};

const LEVEL_OPTIONS = ['', 'L1', 'L2', 'L3', 'L4', 'L5', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10'];

const EMPTY_POSITION = {
  jobTitle: '', departmentId: '', skillsRequired: [], grades: [], location: '',
  budgetMin: '', budgetMax: '', status: 'open',
};

const EMPTY_CANDIDATE = {
  name: '', email: '', phone: '', currentCtc: '', expectedCtc: '',
  noticePeriodDays: '', status: 'applied',
};

export default function Recruitment() {
  const [positions, setPositions] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [skillsets, setSkillsets] = useState([]);
  const [metaProducts, setMetaProducts] = useState([]);
  const [metaModules, setMetaModules] = useState([]);
  const [grades, setGrades] = useState([]);
  const [loading, setLoading] = useState(true);
  // Skill filter state
  const [skillFilterProduct, setSkillFilterProduct] = useState('');
  const [skillFilterModule, setSkillFilterModule] = useState('');
  // Header-level min/max for "Copy to All"
  const [headerMinLevel, setHeaderMinLevel] = useState('');
  const [headerMaxLevel, setHeaderMaxLevel] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_POSITION });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // Detail view
  const [detailPos, setDetailPos] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [candModal, setCandModal] = useState(false);
  const [editingCand, setEditingCand] = useState(null);
  const [candForm, setCandForm] = useState({ ...EMPTY_CANDIDATE });
  const { dirty, markDirty, resetDirty } = useFormDirty();

  const fetchPositions = useCallback(async () => {
    try {
      const res = await apiFetch('/api/recruitment');
      if (res.ok) {
        const data = await res.json();
        setPositions(data.positions || []);
      }
    } catch (err) { /* handled */ }
    setLoading(false);
  }, []);

  const fetchDepartments = useCallback(async () => {
    try {
      const res = await apiFetch('/api/employees/departments');
      if (res.ok) {
        const data = await res.json();
        setDepartments(data.departments || []);
      }
    } catch (err) { /* handled */ }
  }, []);

  const fetchMetadata = useCallback(async () => {
    try {
      const [cascadeRes, gradeRes] = await Promise.allSettled([
        apiFetch('/api/metadata/cascade'),
        apiFetch('/api/employees/grades'),
      ]);
      if (cascadeRes.status === 'fulfilled' && cascadeRes.value.ok) {
        const data = await cascadeRes.value.json();
        setMetaProducts(data.products || []);
        setMetaModules(data.modules || []);
        setSkillsets(data.skillsets || []);
      }
      if (gradeRes.status === 'fulfilled' && gradeRes.value.ok) {
        const data = await gradeRes.value.json();
        setGrades((data.grades || []).filter(g => g.status === 'Active'));
      }
    } catch (err) { /* handled */ }
  }, []);

  useEffect(() => { fetchPositions(); fetchDepartments(); fetchMetadata(); }, [fetchPositions, fetchDepartments, fetchMetadata]);

  async function fetchCandidates(pos) {
    setDetailPos(pos);
    try {
      const res = await apiFetch(`/api/recruitment/${pos.id}/candidates`);
      if (res.ok) {
        const data = await res.json();
        setCandidates(data.candidates || []);
      }
    } catch (err) { setCandidates([]); }
  }

  // Position CRUD
  function openCreate() {
    setEditing(null);
    setForm({ ...EMPTY_POSITION, skillsRequired: [], grades: [] });
    setErrors({});
    setSkillFilterProduct('');
    setSkillFilterModule('');
    setHeaderMinLevel('');
    setHeaderMaxLevel('');
    resetDirty();
    setModalOpen(true);
  }

  function openEdit(pos) {
    setEditing(pos);
    const skills = pos.skills_required;
    let skillsArr = [];
    if (typeof skills === 'string') {
      try { skillsArr = JSON.parse(skills); } catch { skillsArr = skills.split(',').map(s => s.trim()).filter(Boolean); }
    } else if (Array.isArray(skills)) {
      skillsArr = skills;
    }
    // Normalize: old format was string[], new format is {name, minLevel, maxLevel}[]
    skillsArr = skillsArr.map(s => typeof s === 'string' ? { name: s, minLevel: '', maxLevel: '' } : s);
    // Parse grades
    let gradesArr = [];
    const rawGrades = pos.grades;
    if (typeof rawGrades === 'string') {
      try { gradesArr = JSON.parse(rawGrades); } catch { gradesArr = []; }
    } else if (Array.isArray(rawGrades)) {
      gradesArr = rawGrades;
    }
    setForm({
      jobTitle: pos.job_title || '',
      departmentId: pos.department_id || '',
      skillsRequired: skillsArr,
      grades: gradesArr,
      location: pos.location || '',
      budgetMin: pos.budget_min || '',
      budgetMax: pos.budget_max || '',
      status: pos.status || 'open',
    });
    setSkillFilterProduct('');
    setSkillFilterModule('');
    setHeaderMinLevel('');
    setHeaderMaxLevel('');
    setErrors({});
    resetDirty();
    setModalOpen(true);
  }

  function handleField(name, value) {
    setForm(prev => ({ ...prev, [name]: value }));
    markDirty();
  }

  async function handleSave() {
    if (!form.jobTitle.trim()) {
      setErrors({ jobTitle: 'Job title is required' });
      return;
    }
    setSaving(true);
    try {
      const body = {
        ...form,
        skillsRequired: Array.isArray(form.skillsRequired) ? form.skillsRequired : [],
        grades: Array.isArray(form.grades) ? form.grades : [],
      };
      const url = editing ? `/api/recruitment/${editing.id}` : '/api/recruitment';
      const method = editing ? 'PUT' : 'POST';
      const res = await apiFetch(url, { method, body: JSON.stringify(body) });
      if (res.ok) {
        resetDirty();
        setModalOpen(false);
        fetchPositions();
      } else {
        const data = await res.json();
        setErrors({ _general: data.error || 'Failed to save' });
      }
    } catch (err) {
      setErrors({ _general: 'Network error' });
    }
    setSaving(false);
  }

  // Candidate CRUD
  function openCandCreate() {
    setEditingCand(null);
    setCandForm({ ...EMPTY_CANDIDATE });
    resetDirty();
    setCandModal(true);
  }

  async function handleCandSave() {
    if (!candForm.name.trim()) return;
    try {
      const url = editingCand
        ? `/api/recruitment/${detailPos.id}/candidates/${editingCand.id}`
        : `/api/recruitment/${detailPos.id}/candidates`;
      const method = editingCand ? 'PUT' : 'POST';
      const res = await apiFetch(url, { method, body: JSON.stringify(candForm) });
      if (res.ok) {
        resetDirty();
        setCandModal(false);
        fetchCandidates(detailPos);
      }
    } catch (err) { /* ignore */ }
  }

  async function updateCandStatus(cand, newStatus) {
    try {
      await apiFetch(`/api/recruitment/${detailPos.id}/candidates/${cand.id}`, {
        method: 'PUT', body: JSON.stringify({ status: newStatus }),
      });
      fetchCandidates(detailPos);
    } catch (err) { /* ignore */ }
  }

  // Table columns
  const columns = [
    { key: 'job_title', label: 'Position', sortable: true },
    { key: 'department_name', label: 'Department', sortable: true },
    { key: 'location', label: 'Location', sortable: true },
    {
      key: 'budget_min', label: 'Budget Range',
      render: (val, row) => {
        if (!row.budget_min && !row.budget_max) return '--';
        return `${row.budget_min || 0} - ${row.budget_max || 0}`;
      }
    },
    {
      key: 'status', label: 'Status', sortable: true,
      render: (val) => <span className={`badge ${STATUS_BADGES[val] || 'badge-gray'}`}>{val}</span>
    },
    {
      key: 'opened_date', label: 'Opened', sortable: true,
      render: (val) => formatDate(val, '--')
    },
  ];

  const tableActions = [
    { label: 'View', icon: '\u{1F441}', onClick: (row) => fetchCandidates(row) },
    { label: 'Edit', icon: 'edit', onClick: openEdit },
  ];

  // DETAIL: Candidate pipeline
  if (detailPos) {
    const groupedCandidates = {};
    CANDIDATE_STATUS_OPTIONS.forEach(s => { groupedCandidates[s] = []; });
    candidates.forEach(c => {
      const s = c.status || 'applied';
      if (!groupedCandidates[s]) groupedCandidates[s] = [];
      groupedCandidates[s].push(c);
    });

    return (
      <div>
        <button className="btn btn-secondary btn-sm" style={{ marginBottom: 16, marginTop: -40 }}
          onClick={() => setDetailPos(null)}>
          {'\u2190'} Back to Positions
        </button>

        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-header">
            <div>
              <h2>{detailPos.job_title}</h2>
              <div className="meta" style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                <span className={`badge ${STATUS_BADGES[detailPos.status] || 'badge-gray'}`}>{detailPos.status}</span>
                {detailPos.department_name && <span style={{ color: '#64748B' }}>{detailPos.department_name}</span>}
                {detailPos.location && <span style={{ color: '#64748B' }}>{detailPos.location}</span>}
              </div>
            </div>
            <button className="btn btn-primary btn-sm" onClick={openCandCreate}>+ Add Candidate</button>
          </div>
        </div>

        {/* Pipeline view */}
        <div style={{ display: 'flex', gap: 12, overflowX: 'auto', paddingBottom: 8 }}>
          {CANDIDATE_STATUS_OPTIONS.map(status => (
            <div key={status} style={{ minWidth: 200, flex: '1 0 200px' }}>
              <div style={{ background: '#F1F5F9', borderRadius: 8, padding: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span style={{ fontWeight: 600, fontSize: 13, textTransform: 'capitalize' }}>{status}</span>
                  <span className={`badge ${CANDIDATE_BADGES[status] || 'badge-gray'}`}>{groupedCandidates[status].length}</span>
                </div>
                {groupedCandidates[status].map(c => (
                  <div key={c.id} style={{
                    background: '#fff', borderRadius: 6, padding: 10, marginBottom: 8,
                    border: '1px solid #E2E8F0', cursor: 'pointer',
                  }}>
                    <div style={{ fontWeight: 500, fontSize: 13 }}>{c.name}</div>
                    {c.email && <div style={{ fontSize: 11, color: '#64748B' }}>{c.email}</div>}
                    {c.expected_ctc && <div style={{ fontSize: 11, color: '#64748B' }}>Exp CTC: {c.expected_ctc}</div>}
                    <div style={{ display: 'flex', gap: 4, marginTop: 6, flexWrap: 'wrap' }}>
                      {CANDIDATE_STATUS_OPTIONS.filter(s => s !== status).map(s => (
                        <button key={s} className="btn btn-secondary"
                          style={{ fontSize: 10, padding: '2px 6px', textTransform: 'capitalize' }}
                          onClick={() => updateCandStatus(c, s)}>
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
                {groupedCandidates[status].length === 0 && (
                  <div style={{ textAlign: 'center', color: '#94A3B8', fontSize: 12, padding: 12 }}>No candidates</div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Candidate Modal */}
        <Modal title={editingCand ? 'Edit Candidate' : 'Add Candidate'} isOpen={candModal} onClose={() => setCandModal(false)} size="md" dirty={dirty}>
          <FormField label="Name" name="name" value={candForm.name} onChange={(n, v) => { setCandForm(p => ({ ...p, [n]: v })); markDirty(); }} required />
          <div className="form-row">
            <FormField label="Email" name="email" type="email" value={candForm.email} onChange={(n, v) => { setCandForm(p => ({ ...p, [n]: v })); markDirty(); }} />
            <FormField label="Phone" name="phone" value={candForm.phone} onChange={(n, v) => { setCandForm(p => ({ ...p, [n]: v })); markDirty(); }} />
          </div>
          <div className="form-row">
            <FormField label="Current CTC" name="currentCtc" type="number" value={candForm.currentCtc} onChange={(n, v) => { setCandForm(p => ({ ...p, [n]: v })); markDirty(); }} />
            <FormField label="Expected CTC" name="expectedCtc" type="number" value={candForm.expectedCtc} onChange={(n, v) => { setCandForm(p => ({ ...p, [n]: v })); markDirty(); }} />
          </div>
          <div className="form-row">
            <FormField label="Notice Period (days)" name="noticePeriodDays" type="number" value={candForm.noticePeriodDays} onChange={(n, v) => { setCandForm(p => ({ ...p, [n]: v })); markDirty(); }} />
            <FormField label="Status" name="status" type="select" value={candForm.status} onChange={(n, v) => { setCandForm(p => ({ ...p, [n]: v })); markDirty(); }} options={CANDIDATE_STATUS_OPTIONS} />
          </div>
          <div className="form-actions">
            <button className="btn btn-secondary" onClick={() => setCandModal(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={handleCandSave}>{editingCand ? 'Update' : 'Add'} Candidate</button>
          </div>
        </Modal>
      </div>
    );
  }

  // LIST VIEW
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16, marginTop: -40 }}>
        <button className="btn btn-primary" onClick={openCreate}>+ New Position</button>
      </div>

      <DataTable
        columns={columns}
        data={positions}
        actions={tableActions}
        loading={loading}
        emptyMessage="No open positions. Create your first recruitment position."
        emptyIcon={'\u{1F4CB}'}
        onRowClick={(row) => fetchCandidates(row)}
      />

      <Modal title={editing ? 'Edit Position' : 'New Position'} isOpen={modalOpen} onClose={() => setModalOpen(false)} size="md" dirty={dirty}>
        {errors._general && (
          <div style={{ background: '#FEF2F2', color: '#DC2626', padding: '10px 14px', borderRadius: 6, marginBottom: 16, fontSize: 13 }}>
            {errors._general}
          </div>
        )}
        <FormField label="Job Title" name="jobTitle" value={form.jobTitle} onChange={handleField} error={errors.jobTitle} required />
        <div className="form-row">
          <FormField label="Department" name="departmentId" type="select" value={form.departmentId} onChange={handleField}
            options={departments.map(d => ({ value: d.id, label: d.name }))} />
          <FormField label="Location" name="location" value={form.location} onChange={handleField} />
        </div>
        {/* Grades (multi-select) */}
        <div className="form-group">
          <label className="form-label">Eligible Grades</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '8px 10px',
            border: '1px solid rgba(255,255,255,0.15)', borderRadius: 6,
            background: 'rgba(255,255,255,0.05)', minHeight: 36 }}>
            {(form.grades || []).map(gid => {
              const g = grades.find(x => x.id === gid);
              return (
                <span key={gid} style={{ display: 'inline-flex', alignItems: 'center', gap: 4,
                  background: 'rgba(191,95,255,0.15)', color: '#BF5FFF', padding: '3px 10px',
                  borderRadius: 12, fontSize: 12, fontWeight: 500 }}>
                  {g ? g.name : gid}
                  <span style={{ cursor: 'pointer', marginLeft: 2, fontSize: 14, lineHeight: 1 }}
                    onClick={() => handleField('grades', form.grades.filter(x => x !== gid))}>×</span>
                </span>
              );
            })}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
            {grades.filter(g => !(form.grades || []).includes(g.id)).map(g => (
              <button key={g.id} type="button" className="btn btn-secondary"
                style={{ fontSize: 11, padding: '3px 10px', borderRadius: 12 }}
                onClick={() => handleField('grades', [...(form.grades || []), g.id])}>
                + {g.name}
              </button>
            ))}
          </div>
          {grades.length === 0 && (
            <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>No grades defined yet. Add grades in HCM → Grades.</div>
          )}
        </div>
        {/* Required Skills with Product/Skill Group filters and min/max levels */}
        <div className="form-group">
          <label className="form-label">Required Skills</label>
          {/* Filters */}
          <div className="form-row" style={{ marginBottom: 8 }}>
            <FormField label="" name="_skillProd" type="select" placeholder="Filter by Product"
              value={skillFilterProduct} onChange={(n, v) => { setSkillFilterProduct(v); setSkillFilterModule(''); }}
              options={metaProducts.map(p => ({ value: p.id, label: p.name }))} />
            <FormField label="" name="_skillMod" type="select" placeholder="Filter by Skill Group"
              value={skillFilterModule} onChange={(n, v) => setSkillFilterModule(v)}
              options={metaModules
                .filter(m => !skillFilterProduct || m.product_id === skillFilterProduct)
                .map(m => ({ value: m.id, label: m.name }))} />
          </div>
          {/* Available skills to add */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 10 }}>
            {skillsets
              .filter(s => {
                // Filter by product/module
                if (skillFilterModule && s.module_id !== skillFilterModule) return false;
                if (skillFilterProduct && !skillFilterModule) {
                  const mod = metaModules.find(m => m.id === s.module_id);
                  if (!mod || mod.product_id !== skillFilterProduct) return false;
                }
                // Exclude already selected
                return !(form.skillsRequired || []).some(sel => sel.name === s.name);
              })
              .map(s => {
                const mod = metaModules.find(m => m.id === s.module_id);
                return (
                  <button key={s.id} type="button" className="btn btn-secondary"
                    style={{ fontSize: 11, padding: '3px 10px', borderRadius: 12 }}
                    onClick={() => { handleField('skillsRequired', [...(form.skillsRequired || []), { name: s.name, minLevel: headerMinLevel, maxLevel: headerMaxLevel }]); }}>
                    + {s.name}{mod ? ` (${mod.name})` : ''}
                  </button>
                );
              })}
            {skillsets.length === 0 && (
              <div style={{ fontSize: 12, color: '#888' }}>No skills defined yet. Add skills in System → Metadata → Skillsets.</div>
            )}
          </div>
          {/* Header-level min/max + Copy to All */}
          {(form.skillsRequired || []).length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, padding: '6px 10px',
              background: 'rgba(0,224,255,0.06)', borderRadius: 6, border: '1px solid rgba(0,224,255,0.12)' }}>
              <span style={{ fontSize: 12, color: '#888', whiteSpace: 'nowrap' }}>Default Levels:</span>
              <select className="form-control" style={{ width: 80, padding: '3px 6px', fontSize: 12 }}
                value={headerMinLevel} onChange={e => setHeaderMinLevel(e.target.value)}>
                <option value="">Min</option>
                {LEVEL_OPTIONS.filter(Boolean).map(l => <option key={l} value={l}>{l}</option>)}
              </select>
              <select className="form-control" style={{ width: 80, padding: '3px 6px', fontSize: 12 }}
                value={headerMaxLevel} onChange={e => setHeaderMaxLevel(e.target.value)}>
                <option value="">Max</option>
                {LEVEL_OPTIONS.filter(Boolean).map(l => <option key={l} value={l}>{l}</option>)}
              </select>
              <button type="button" className="btn btn-secondary" style={{ fontSize: 11, padding: '3px 10px', whiteSpace: 'nowrap' }}
                onClick={() => {
                  handleField('skillsRequired', (form.skillsRequired || []).map(sk => ({
                    ...sk, minLevel: headerMinLevel || sk.minLevel, maxLevel: headerMaxLevel || sk.maxLevel,
                  })));
                }}>
                Copy to All
              </button>
            </div>
          )}
          {/* Selected skills table */}
          {(form.skillsRequired || []).length > 0 && (
            <div style={{ border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: 'rgba(255,255,255,0.04)' }}>
                    <th style={{ textAlign: 'left', padding: '6px 10px', color: '#888', fontWeight: 500 }}>Skill</th>
                    <th style={{ width: 90, padding: '6px 8px', color: '#888', fontWeight: 500 }}>Min Level</th>
                    <th style={{ width: 90, padding: '6px 8px', color: '#888', fontWeight: 500 }}>Max Level</th>
                    <th style={{ width: 36 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {(form.skillsRequired || []).map((sk, idx) => (
                    <tr key={idx} style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                      <td style={{ padding: '5px 10px', color: '#00e0ff' }}>{sk.name}</td>
                      <td style={{ padding: '4px 4px' }}>
                        <select className="form-control" style={{ padding: '2px 4px', fontSize: 12 }}
                          value={sk.minLevel || ''} onChange={e => {
                            const updated = [...form.skillsRequired];
                            updated[idx] = { ...sk, minLevel: e.target.value };
                            handleField('skillsRequired', updated);
                          }}>
                          <option value="">—</option>
                          {LEVEL_OPTIONS.filter(Boolean).map(l => <option key={l} value={l}>{l}</option>)}
                        </select>
                      </td>
                      <td style={{ padding: '4px 4px' }}>
                        <select className="form-control" style={{ padding: '2px 4px', fontSize: 12 }}
                          value={sk.maxLevel || ''} onChange={e => {
                            const updated = [...form.skillsRequired];
                            updated[idx] = { ...sk, maxLevel: e.target.value };
                            handleField('skillsRequired', updated);
                          }}>
                          <option value="">—</option>
                          {LEVEL_OPTIONS.filter(Boolean).map(l => <option key={l} value={l}>{l}</option>)}
                        </select>
                      </td>
                      <td style={{ padding: '4px', textAlign: 'center' }}>
                        <span style={{ cursor: 'pointer', color: '#ff4444', fontSize: 16, lineHeight: 1 }}
                          onClick={() => handleField('skillsRequired', form.skillsRequired.filter((_, i) => i !== idx))}>×</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        <div className="form-row">
          <FormField label="Budget Min" name="budgetMin" type="number" value={form.budgetMin} onChange={handleField} />
          <FormField label="Budget Max" name="budgetMax" type="number" value={form.budgetMax} onChange={handleField} />
        </div>
        <div className="form-actions">
          <button className="btn btn-secondary" onClick={() => setModalOpen(false)}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : (editing ? 'Update Position' : 'Create Position')}
          </button>
        </div>
      </Modal>
    </div>
  );
}
